import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'
import prepareHeaders from '../utils/prepareHeaders'

const POLICY_NAMED_LOCATIONS_URL = '/secure/policies/named-locations'

export const POLICY_NAMED_LOCATIONS_TAG = 'PolicyNamedLocations'

export const getPolicyNamedLocationsTag = (customerId) => ({ type: POLICY_NAMED_LOCATIONS_TAG, id: String(customerId) })

const sortByDisplayName = (a, b) => a.displayName.localeCompare(b.displayName)

export const namedLocationTemplatesApi = createApi({
  reducerPath: 'namedLocationTemplates',
  tagTypes: [POLICY_NAMED_LOCATIONS_TAG],
  baseQuery: fetchBaseQuery({
    prepareHeaders,
    baseUrl: process.env.REACT_APP_AUGMENTT_API_URL,
  }),
  endpoints: (builder) => ({
    // Cached per customerId, so a late response for one tenant never fills another tenant's list (AP-13766)
    getPolicyNamedLocations: builder.query({
      query: ({ customerId }) => ({ url: POLICY_NAMED_LOCATIONS_URL, params: { customerId } }),
      transformResponse: (response) => [...(response ?? [])].sort(sortByDisplayName),
      providesTags: (_, __, { customerId }) => [getPolicyNamedLocationsTag(customerId)],
    }),
    // Create/update/delete only start a hub task; callers invalidate the list once the task finishes
    createPolicyNamedLocation: builder.mutation({
      query: ({ customerId, userId, data }) => ({
        url: POLICY_NAMED_LOCATIONS_URL,
        method: 'POST',
        params: { customerId, userId },
        body: data,
      }),
    }),
    updatePolicyNamedLocation: builder.mutation({
      query: ({ id, customerId, userId, data }) => ({
        url: `${POLICY_NAMED_LOCATIONS_URL}/${id}`,
        method: 'PUT',
        params: { customerId, userId },
        body: data,
      }),
    }),
    deletePolicyNamedLocation: builder.mutation({
      query: ({ id, customerId, userId }) => ({
        url: `${POLICY_NAMED_LOCATIONS_URL}/${id}`,
        method: 'DELETE',
        params: { customerId, userId },
      }),
    }),
    getNamedLocationTemplateDeployPreview: builder.query({
      query: ({ templateId, customerIds }) => ({
        url: `/secure/policy/template/${templateId}/named-location-deploy/preview`,
        params: { customerIds: customerIds.join(',') },
      }),
    }),
    deployNamedLocationTemplate: builder.mutation({
      query: ({ templateId, customerIds, chosenCandidates, contentChoices, userId }) => ({
        url: `/secure/policy/template/${templateId}/named-location-deploy`,
        method: 'POST',
        params: { userId },
        body: { customerIds, chosenCandidates, contentChoices },
      }),
    }),
  }),
})

export const {
  useGetPolicyNamedLocationsQuery,
  useLazyGetPolicyNamedLocationsQuery,
  useCreatePolicyNamedLocationMutation,
  useUpdatePolicyNamedLocationMutation,
  useDeletePolicyNamedLocationMutation,
  useLazyGetNamedLocationTemplateDeployPreviewQuery,
  useDeployNamedLocationTemplateMutation,
} = namedLocationTemplatesApi
